import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Icon, type IconName } from '@/design-system';
import { BottleImage } from '@/components/BottleCard';
import type { VmpDiscoverProduct } from '@/lib/api/v1';

interface CatalogProductCardProps {
  product: VmpDiscoverProduct;
  action?: ReactNode;
}

export function catalogProductImageUrl(product: VmpDiscoverProduct): string | undefined {
  const productImage = product.images?.find((image) => image.format === 'product')?.url;
  const firstImage = product.images?.find((image) => Boolean(image.url))?.url;
  return productImage ?? firstImage ?? `https://bilder.vinmonopolet.no/cache/300x300-0/${product.code}-1.jpg`;
}

export function CatalogProductCard({ product, action }: CatalogProductCardProps) {
  const image = catalogProductImageUrl(product);
  const price = product.price?.formattedValue;
  const country = product.main_country?.name;
  const region = product.district?.name;
  const subRegion = product.sub_District?.name;
  const category = product.main_sub_category?.name ?? product.main_category?.name;
  const selection = product.product_selection;
  const isSpecial = selection === 'Spesialutvalg';
  const details = [
    category,
    product.alcohol?.formattedValue,
    product.volume?.formattedValue,
  ].filter(Boolean);

  return (
    <article className="ui-card ui-card--interactive grid min-h-full gap-3 overflow-hidden p-3">
      <Link to={`/bottle/${product.code}`} className="grid gap-3">
        <ProductImageStage product={product} image={image} />

        <div className="grid gap-2 px-1">
          <div className="flex flex-wrap items-center gap-1.5">
            {isSpecial ? (
              <span className="rounded-sm bg-ruby px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow-cellar">
                Spesial
              </span>
            ) : selection ? (
              <Badge tone="mist">{selection}</Badge>
            ) : null}
            {product.sustainable && (
              <Badge tone="success" className="gap-1">
                <Icon name="sparkles" size={10} /> bærekraftig
              </Badge>
            )}
          </div>

          <div className="grid gap-1">
            <h3 className="font-serif text-lg leading-tight text-parchment line-clamp-2">{product.name}</h3>
            <p className="min-h-[2rem] text-xs leading-relaxed text-mist line-clamp-2">
              {[subRegion, region, country].filter(Boolean).join(' · ') || 'Vinmonopolet-katalogen'}
            </p>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {details.map((detail) => (
              <span
                key={detail}
                className="rounded-sm border border-line bg-overlay-soft px-2 py-1 text-[11px] font-medium text-parchment"
              >
                {detail}
              </span>
            ))}
          </div>

          <div className="flex items-end justify-between gap-3 pt-1">
            <span className="text-[10px] uppercase tracking-widest text-mist">#{product.code}</span>
            {price && (
              <span className="rounded-sm bg-parchment px-2.5 py-1 text-sm font-semibold tabular-nums text-ink shadow-cellar">
                {price}
              </span>
            )}
          </div>
        </div>
      </Link>

      {action ? <div className="px-1 pb-1">{action}</div> : null}
    </article>
  );
}

/**
 * Live Vinmonopolet discovery search results aren't mirrored to our own
 * /media/catalog-images yet (that only happens once a product is actually
 * imported/added — see backend/src/scripts/mirror-catalog-images.ts), so
 * these are always raw hotlinked CDN photos on a white background. Shared
 * `BottleImage`'s `mode="lightbox"` still handles that gracefully (cream
 * backdrop + multiply blend hides the white rectangle) even though its
 * cutout variant can't apply to a non-mirrored URL.
 */
function ProductImageStage({ product, image }: { product: VmpDiscoverProduct; image?: string }) {
  const [failed, setFailed] = useState(false);
  const category = product.main_sub_category?.name ?? product.main_category?.name ?? 'Produkt';

  if (!image || failed) {
    return (
      <div className="relative grid h-36 place-items-center overflow-hidden rounded-md border border-line bg-ink-soft sm:h-40 lg:h-36 xl:h-40 2xl:h-44">
        <ProductImageFallback category={category} />
      </div>
    );
  }

  return (
    <div className="relative grid h-36 place-items-center overflow-hidden rounded-md border border-line bg-ink-soft p-3 sm:h-40 lg:h-36 xl:h-40 2xl:h-44">
      <BottleImage src={image} alt={product.name} iconSize={48} mode="lightbox" onFail={() => setFailed(true)} />
    </div>
  );
}

function ProductImageFallback({ category }: { category: string }) {
  const icon = categoryIcon(category);
  return (
    <div
      className="absolute inset-0 grid place-items-center p-4 text-center"
      style={{
        background:
          'linear-gradient(145deg, rgba(230, 180, 107, 0.22), rgba(107, 29, 40, 0.22)), linear-gradient(180deg, var(--ui-color-ink-soft), var(--ui-color-ink-muted))',
      }}
    >
      <div className="grid gap-3">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-md border border-candle/35 bg-candle/10 text-candle shadow-cellar">
          <Icon name={icon} size={34} />
        </div>
        <div className="grid gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-mist">Bilde mangler</span>
          <span className="font-serif text-lg leading-tight text-parchment">{category}</span>
        </div>
      </div>
    </div>
  );
}

function categoryIcon(category: string): IconName {
  const normalized = category.toLowerCase();
  if (normalized.includes('øl')) return 'beer';
  if (normalized.includes('sider')) return 'grape';
  if (normalized.includes('brennevin') || normalized.includes('sprit')) return 'spirits';
  if (normalized.includes('rosé')) return 'cherry';
  return 'bottle';
}
